/**
 * Level Tool - Level Creation
 *
 * Click-click tool: first click sets the level's elevation (Z height),
 * second click sets its horizontal extent. The datum is registered through
 * the kernel's addLevel() so it lands in project.levels — the map the
 * LevelRenderer actually draws from — and a Levels element mirrors the
 * datum for properties/selection workflows.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface LevelGeometryData {
    start: [number, number, number];
    end: [number, number, number];
    elevation: number;
}

export interface LevelElement extends Element {
    category: 'Levels';
    geometryData: LevelGeometryData;
    elevation: number;
}

export class LevelTool extends BaseTool {
    id = 'level';
    name = 'Level';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    /**
     * Tools are module-level singletons: staged sketch state survives
     * deactivation (Esc mid-draw) and would leak into the NEXT level
     * session, so the first click would draw from a stale anchor.
     * Reset on activate.
     */
    protected onActivate(): void {
        this.startPoint = null;
        this.currentPoint = null;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
            this.pushPreview();
        } else {
            this.createLevel(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.pushPreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private pushPreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'level',
                active: true,
                start: this.startPoint,
                end: this.currentPoint,
                color: PREVIEW_COLOR
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createLevel(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        // Elevation is the Z height in meters (world units are meters).
        const elevation = start.z;

        // The datum map the LevelRenderer draws from. Elevation is stored in
        // millimeters there (LevelRenderer divides by 1000).
        const name = this.nextLevelName();
        this.context.bimStore.addLevel(name, elevation * 1000);

        // Mirror datum as a Levels element so selection/properties workflows
        // can resolve it from project.elements.
        const geometryData: LevelGeometryData = {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            elevation
        };

        const level: LevelElement = {
            id: uuidv4(),
            category: 'Levels',
            type: 'Level',
            elevation,
            parameters: {
                'Elevation': `${elevation.toFixed(2)} m`,
                'Name': name
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(level);
    }

    private nextLevelName(): string {
        if (!this.context) return 'Level 1';
        let max = 0;
        this.context.bimStore.project.levels.forEach(level => {
            const match = /^Level (\d+)$/.exec(level.name);
            if (match) max = Math.max(max, parseInt(match[1], 10));
        });
        return `Level ${max + 1}`;
    }
}

// BIM-style datum color for the sketch preview (kept here as the tool-side
// token; the viewport preview branch reads the same value from PREVIEW_COLORS).
const PREVIEW_COLOR = '#2f7bd6';
